
import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';
import {
  useTenantBranding,
  type TenantBranding,
  DEFAULT_PRIMARY_COLOR,
} from '@/hooks/use-tenant-branding';
import { getCurrentTenantId } from '@/lib/tenant';

interface BrandingContextType {
  branding: TenantBranding | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  logoUrl: string | null;
  fallbackLogoUrl: string;
  businessName: string;
  primaryColor: string;
}

const BrandingContext = createContext<BrandingContextType | undefined>(
  undefined
);

interface BrandingProviderProps {
  children: ReactNode;
}

function getTenantFallbackLogo(tenantId: string | null): string {
  const normalized = (tenantId || '').toLowerCase().trim();
  if (normalized === 'flex' || normalized === 'flexgym') {
    return '/gym1.png';
  }
  return '/logo-with-text.png';
}

/**
 * Loading screen component shown while branding loads
 */
function BrandingLoadingScreen() {
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-300"
      style={{ backgroundColor: '#000' }}
    >
      <div className="flex flex-col items-center gap-4">
        {/* Simple spinner with neutral colors */}
        <div
          className="h-10 w-10 animate-spin rounded-full border-4"
          style={{ borderColor: '#333', borderTopColor: '#888' }}
        />
        <p style={{ color: '#888', fontSize: '14px' }}>Loading...</p>
      </div>
    </div>
  );
}

export function BrandingProvider({ children }: BrandingProviderProps) {
  const { branding, loading, error, refetch } = useTenantBranding();
  const [mounted, setMounted] = useState(false);
  const [tenantId, setTenantId] = useState<string | null>(null);

  // Check for tenant ID after mount (client-side only)
  useEffect(() => {
    setMounted(true);
    setTenantId(getCurrentTenantId());
  }, []);

  // Show loading screen if:
  // 1. We're mounted (client-side)
  // 2. We have a tenant ID (needs branding)
  // 3. Branding is still loading
  const showLoadingScreen = mounted && !!tenantId && loading;
  const fallbackLogoUrl = getTenantFallbackLogo(tenantId);

  const value: BrandingContextType = {
    branding,
    loading,
    error,
    refetch,
    logoUrl: branding?.logo || fallbackLogoUrl,
    fallbackLogoUrl,
    businessName: branding?.businessName || 'THE NOT FIT',
    primaryColor: branding?.primaryColor || DEFAULT_PRIMARY_COLOR,
  };

  // Before mount, show loading screen for tenant subdomains
  // This prevents flash of default theme
  if (!mounted) {
    return (
      <BrandingContext.Provider value={value}>
        <BrandingLoadingScreen />
      </BrandingContext.Provider>
    );
  }

  return (
    <BrandingContext.Provider value={value}>
      {showLoadingScreen && <BrandingLoadingScreen />}
      <div style={{ visibility: showLoadingScreen ? 'hidden' : 'visible' }}>
        {children}
      </div>
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}

export { type TenantBranding };
